import {
  Ban,
  CheckCircle2,
  Circle,
  HelpCircle,
  Hourglass,
  Info,
  Loader2,
  MoreVertical,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TrailerInformationPopup } from '@/components/trailerInformation';
import { useToast } from '@/components/ui';
import { i18n } from '@/i18n';
import { GetTrailersListAsync, updateRedTagTrailer } from '@/service/MoveTaskService';
import { store, useAppSelector, selectSettings } from '@/store';
import { cn } from '@/utils/cn';

import type {
  LockReasonCodeType,
  TrailerMasterData,
  TrailerModel as APITrailerModel,
} from '@/service/types/movetasktypes';
import type { TrailerModel } from '@/service/types/trailerTypes';

// ─────────────────────────────────────────────────────────────────
// Helpers — resolve codes to names from TRAILER_MASTER_DATA
// ─────────────────────────────────────────────────────────────────

function getTrailerMasterData(): TrailerMasterData | null {
  const settings = store.getState().global.settings;
  const json = settings?.TRAILER_MASTER_DATA ?? '';
  if (!json) return null;
  try {
    return JSON.parse(json) as TrailerMasterData;
  } catch {
    return null;
  }
}

/**
 * Lock reason codes for display (dropdown options, damage reason column/popup).
 * Prefers TRAILER_MASTER_DATA_SPANISH when the app is in Spanish, falling back to English.
 * Status/condition codes are intentionally left on English data — they drive internal
 * "Damaged" comparisons elsewhere and must not be swapped by language.
 */
function getLockReasonCodes(): LockReasonCodeType[] {
  const settings = store.getState().global.settings;

  if (i18n.language === 'es' && settings?.TRAILER_MASTER_DATA_SPANISH) {
    try {
      const parsed: unknown = JSON.parse(settings.TRAILER_MASTER_DATA_SPANISH);
      const lockReasonCodes = (parsed as Partial<TrailerMasterData> | null)?.Lock_Reason_Codes;
      if (Array.isArray(lockReasonCodes)) return lockReasonCodes;
    } catch {
      // Fall through to English
    }
  }

  return getTrailerMasterData()?.Lock_Reason_Codes ?? [];
}

function resolveStatusName(code: string, masterData: TrailerMasterData | null): string {
  if (!code || !masterData) return code;
  const match = masterData.Trailer_Status.find((s) => s.Status_Code === code);
  return match?.Status_Name ?? code;
}

function resolveConditionName(code: string, masterData: TrailerMasterData | null): string {
  if (!code || !masterData) return code;
  const match = masterData.Trailer_Conditions_Types.find((c) => String(c.condition_code) === code);
  return match?.condition_name ?? code;
}

function resolveLockReasonName(code: string, lockReasonCodes: LockReasonCodeType[]): string {
  if (!code) return code;
  const match = lockReasonCodes.find((r) => String(r.Reason_Code) === code);
  return match?.Reason_Name ?? code;
}

/** Map API TrailerModel (PascalCase) to UI TrailerModel (camelCase) */
function mapAPITrailerToUI(
  apiTrailer: APITrailerModel,
  masterData: TrailerMasterData | null,
  lockReasonCodes: LockReasonCodeType[]
): TrailerModel {
  const statusName = resolveStatusName(apiTrailer.TrailerStatus, masterData);
  const conditionName = resolveConditionName(apiTrailer.TrailerCondition, masterData);
  const lockReasonName = resolveLockReasonName(apiTrailer.LockReasonCode, lockReasonCodes);
  const location = `${apiTrailer.CurrentLocationZone} - ${apiTrailer.CurrentLocationName} (${apiTrailer.CurrentLocationType})`;

  const displayStatus = conditionName === 'Damaged' || lockReasonName ? 'Damaged' : statusName;

  return {
    trailerId: String(apiTrailer.TrailerId),
    trailerNumber: apiTrailer.Trailer_Name,
    carrierName: apiTrailer.Carrier_Name,
    carrierScac: apiTrailer.Carrier_scac,
    status: displayStatus,
    condition: conditionName || undefined,
    isLocked: apiTrailer.LockReasonCode !== '',
    lockReason: lockReasonName || undefined,
    lockReasonCode: apiTrailer.LockReasonCode,
    location,
    lastUpdated: '',
    dwellTime: apiTrailer.DwellingTime,
    checkInTime: apiTrailer.CheckInTime,
    elapsedMinutes: apiTrailer.DwellingTimeHours ? Number(apiTrailer.DwellingTimeHours) : undefined,
    taskPriority: '',
    overrideReason: undefined,
    customerName: apiTrailer.CustomerName || undefined,
    shipmentNumber: apiTrailer.Shipment || undefined,
    comments: apiTrailer.TrailerComments || undefined,
    damageReason: lockReasonName || undefined,
    scheduledPickup: apiTrailer.ScheduledPickup || undefined,
    driverName: apiTrailer.DriverName || undefined,
    driverCell: apiTrailer.DriverCell || undefined,
    items: apiTrailer.Item || undefined,
    palletsLoaded: apiTrailer.PalletsLoaded ? Number(apiTrailer.PalletsLoaded) : undefined,
    po: apiTrailer.PurchaseOrder || undefined,
    doNumber: apiTrailer.Order || undefined,
    trailerStatus: apiTrailer.TrailerStatus,
    trailerCondition: apiTrailer.TrailerCondition,
  };
}

// ── Auto-refresh interval parser — Matches Xamarin AutoRefreshTimerModel.TrailersListPage ──

function parseAutoRefreshInterval(autoRefreshScreensJson: string | undefined): number {
  if (!autoRefreshScreensJson) return 0;
  try {
    const screens = JSON.parse(autoRefreshScreensJson) as Record<string, number>;
    const intervalSec = screens['TrailersListPage'] ?? 0;
    return intervalSec > 0 ? intervalSec * 1000 : 0;
  } catch {
    return 0;
  }
}

// ── Status icon — Matches Xamarin TrailerStatusToFilterImageConverter + YMSColorConstant ──

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'Damaged':
      return <Ban className="h-[15px] w-[15px] text-[#FC3B3B] shrink-0" aria-hidden="true" />;
    case 'Loaded':
    case 'IB Loaded':
    case 'OB Loaded':
      return (
        <CheckCircle2 className="h-[15px] w-[15px] text-[#00D649] shrink-0" aria-hidden="true" />
      );
    case 'IB Loading':
    case 'OB Loading':
    case 'IB Unloading':
      return <Hourglass className="h-[15px] w-[15px] text-[#EDBE18] shrink-0" aria-hidden="true" />;
    case 'Empty':
      return <Circle className="h-[15px] w-[15px] text-[#333333] shrink-0" aria-hidden="true" />;
    default:
      return <Circle className="h-[15px] w-[15px] text-[#333333] shrink-0" aria-hidden="true" />;
  }
}

// ── Search bar — Matches Xamarin SearchBarAndFilter (ShowFilter=false) ──

interface TrailerSearchBarProps {
  searchText: string;
  onSearchChange: (text: string) => void;
}

function TrailerSearchBar({ searchText, onSearchChange }: TrailerSearchBarProps) {
  const { t } = useTranslation();
  const [localQuery, setLocalQuery] = useState(searchText);

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(localQuery);
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [localQuery, onSearchChange]);

  useEffect(() => {
    setLocalQuery(searchText);
  }, [searchText]);

  return (
    <div className="mx-0 md:mt-[5px]">
      <div className="h-10 md:h-[35px] rounded-[3px] border border-[#D2E3F0] bg-white mx-[10px]">
        <input
          type="text"
          maxLength={100}
          placeholder={t('trailers.search')}
          value={localQuery}
          onChange={(e) => {
            setLocalQuery(e.target.value);
          }}
          className="h-full w-full bg-transparent pl-[10px] pr-3 text-xs font-poppins text-[#3B3B3B] placeholder:text-[#A0AEC0] outline-none"
        />
      </div>
    </div>
  );
}

// ── Actions popup — Matches Xamarin TrailerActionsPopup ──
// Width: 200(d)/180(p), CornerRadius=22
// Conditional: "Edit Damage" when already damaged, "Mark As Damaged" otherwise

interface ActionsPopupProps {
  open: boolean;
  trailer: TrailerModel | undefined;
  onClose: () => void;
  onMarkAsDamaged: () => void;
}

function ActionsPopup({ open, trailer, onClose, onMarkAsDamaged }: ActionsPopupProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [open, onClose]);

  if (!open || !trailer) return null;

  const isDamaged = trailer.status === 'Damaged';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 animate-fade-in"
      onClick={onClose}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="bg-white rounded-[22px] py-4 px-5 w-[200px] shadow-lg animate-popup-scale"
        onClick={(e) => {
          e.stopPropagation();
        }}
        role="dialog"
        aria-modal="true"
        aria-label={t('trailers.actions')}
      >
        <p className="text-sm font-poppins font-bold text-[#333333] text-center mb-3">
          {t('trailers.actions')}
        </p>
        <button
          type="button"
          onClick={() => {
            onClose();
            onMarkAsDamaged();
          }}
          className="w-full py-[6px] px-3 rounded-[10px] bg-[#D3D3D3] shadow text-sm font-poppins font-medium text-[#333333]"
        >
          {isDamaged ? t('trailers.editDamage') : t('trailers.markAsDamaged')}
        </button>
      </div>
    </div>
  );
}

// ── Mark As Damaged dialog — Matches Xamarin MarkAsDamagedPopUp ──

interface MarkAsDamagedDialogProps {
  open: boolean;
  trailer: TrailerModel | undefined;
  onClose: () => void;
  onSubmit: (conditionCode: number | undefined, conditionName: string) => void;
}

function MarkAsDamagedDialog({ open, trailer, onClose, onSubmit }: MarkAsDamagedDialogProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReason, setSelectedReason] = useState<LockReasonCodeType | undefined>(undefined);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [reasonCodes, setReasonCodes] = useState<LockReasonCodeType[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedReason(undefined);
      setDropdownOpen(false);
      setSubmitting(false);
      setReasonCodes(getLockReasonCodes());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [open, onClose]);

  if (!open || !trailer) return null;

  const filteredOptions = reasonCodes.filter((rc) =>
    rc.Reason_Name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const enableSubmit = selectedReason !== undefined && !submitting;

  const handleSubmit = () => {
    if (selectedReason === undefined || submitting) return;
    setSubmitting(true);
    onSubmit(selectedReason.Reason_Code, selectedReason.Reason_Name);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      {/* Frame: Width 450(d)/250(p), CornerRadius=22, Height=300 */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="bg-white rounded-[22px] shadow-xl w-[calc(100vw-32px)] xs:w-[320px] sm:w-[360px] md:w-[450px] p-4 xs:p-5 md:p-6"
        style={{ minHeight: '300px' }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        role="dialog"
        aria-modal="true"
        aria-label={t('trailers.markAsDamaged')}
      >
        {/* Header row — Title + Close icon */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-poppins font-bold text-[#243743]">
            {t('trailers.markAsDamaged')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label={t('common.close')}
          >
            <X className="h-[25px] w-[25px] text-[#445E71]" />
          </button>
        </div>

        {/* Trailer info — number | location with vertical divider */}
        <div className="flex items-start mb-4">
          <div className="flex-1">
            <p className="text-[10px] md:text-xs font-poppins font-bold text-[#8A8A8A]">
              {t('trailers.trailerNumber')}
            </p>
            <p className="text-[10px] md:text-xs font-poppins text-[#8A8A8A] mt-0.5">
              {trailer.trailerNumber || '-'}
            </p>
          </div>
          {/* Vertical separator — Matches Xamarin BoxView WidthRequest=1, #D0D0D0 */}
          <div className="w-px bg-[#D0D0D0] self-stretch mx-3" />
          <div className="flex-1">
            <p className="text-[10px] md:text-xs font-poppins font-bold text-[#8A8A8A]">
              {t('trailers.location')}
            </p>
            <p className="text-[10px] md:text-xs font-poppins text-[#8A8A8A] mt-0.5">
              {trailer.location || '-'}
            </p>
          </div>
        </div>

        {/* Select A Reason — Matches Xamarin QuestionModel + Expander */}
        <div className="mb-6">
          <p className="text-xs md:text-sm font-poppins text-[#243743] mb-2">
            {t('trailers.selectAReason')}
          </p>

          {/* Searchable dropdown — Matches Xamarin Expander with search entry */}
          <div className="relative">
            {selectedReason ? (
              // Selected display — show "{ReasonName} ({ReasonCode})"
              <button
                type="button"
                onClick={() => {
                  setSelectedReason(undefined);
                  setDropdownOpen(true);
                }}
                className="w-full px-3 py-2.5 border border-[#D0D0D0] rounded-lg text-xs md:text-sm font-poppins text-[#0D262E] text-left"
              >
                {selectedReason.Reason_Name} ({String(selectedReason.Reason_Code)})
              </button>
            ) : (
              <div className="grid border border-[#D0D0D0] rounded-lg focus-within:border-[#003366]">
                {!searchQuery && (
                  <span className="col-start-1 row-start-1 px-3 py-2.5 text-xs md:text-sm font-poppins text-[#A0B4C4] pointer-events-none">
                    {t('trailers.searchOrSelect')}
                  </span>
                )}
                <input
                  type="text"
                  value={searchQuery}
                  disabled={submitting}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setDropdownOpen(true);
                  }}
                  onFocus={() => {
                    setDropdownOpen(true);
                  }}
                  className="col-start-1 row-start-1 w-full px-3 py-2.5 bg-transparent text-xs md:text-sm font-poppins text-[#0D262E] outline-none border-none"
                />
              </div>
            )}

            {/* Dropdown list */}
            {dropdownOpen && !selectedReason && (
              <>
                <div
                  className="fixed inset-0 z-0"
                  onClick={() => {
                    setDropdownOpen(false);
                  }}
                  role="presentation"
                />
                <div
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#D0D0D0] rounded-lg max-h-[200px] overflow-y-auto z-10"
                  style={{ scrollbarWidth: 'thin', scrollbarColor: '#D2DAE1 transparent' }}
                >
                  {filteredOptions.length === 0 ? (
                    <p className="px-3 py-3 text-xs md:text-sm font-poppins font-semibold text-[#333333] text-center">
                      {t('trailers.lockReasonNotFound')}
                    </p>
                  ) : (
                    filteredOptions.map((rc) => (
                      <button
                        key={rc.Reason_Code}
                        type="button"
                        onClick={() => {
                          setSelectedReason(rc);
                          setDropdownOpen(false);
                          setSearchQuery('');
                        }}
                        className="w-full text-left px-[10px] py-3 text-xs md:text-sm font-poppins text-[#0D262E] hover:bg-[#D0D0D0]"
                      >
                        {rc.Reason_Name}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer buttons — Matches Xamarin: Cancel (#003366 border) + Submit (#2FA851 / #aaaaaa) */}
        {/* Stack vertically on mobile (<md), side by side on tablet+ */}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-[10px] sm:gap-[15px] px-[10px] py-[5px]">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="w-full sm:w-[157px] h-[40px] rounded-[5px] border border-[#003366] bg-transparent text-xs font-poppins text-[#003366] hover:bg-gray-50 transition-colors"
          >
            {t('trailers.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!enableSubmit}
            className={cn(
              'w-full sm:w-[157px] h-[40px] rounded-[5px] text-xs font-poppins text-white transition-colors',
              enableSubmit ? 'bg-[#2FA851] hover:bg-[#268F45]' : 'bg-[#aaaaaa] cursor-not-allowed'
            )}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mx-auto" />
            ) : (
              t('trailers.submit')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Empty state — Matches Xamarin EmptyListView with EmptyTrailerIllustration ──

function EmptyTrailerState() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-4 xs:gap-6 px-4 py-10 xs:py-16">
      <img
        src="/images/ic_trailer.svg"
        alt=""
        className="h-24 w-24 xs:h-32 xs:w-32 sm:h-40 sm:w-40 opacity-80"
        aria-hidden="true"
      />
      <p className="text-base xs:text-xl md:text-[25px] font-poppins font-bold text-[#243743] text-center">
        {t('trailers.noTrailerFound')}
      </p>
    </div>
  );
}

// ── Main Page Component — Matches Xamarin TrailersListViewModel ──

export function TrailersPage() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const settings = useAppSelector(selectSettings);
  const autoRefreshScreens = settings?.AUTO_REFRESH_SCREENS;

  const [trailers, setTrailers] = useState<TrailerModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrailer, setSelectedTrailer] = useState<TrailerModel | undefined>(undefined);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [legendOpen, setLegendOpen] = useState(false);
  const [actionsTrailer, setActionsTrailer] = useState<TrailerModel | undefined>(undefined);
  const [pendingDamageTrailer, setPendingDamageTrailer] = useState<TrailerModel | undefined>(
    undefined
  );
  const canRefreshRef = useRef(true);

  // ── Fetch trailers — Matches Xamarin GetTrailerList() ──
  const loadTrailers = useCallback(async () => {
    setLoading(true);
    try {
      const masterData = getTrailerMasterData();
      const lockReasonCodes = getLockReasonCodes();
      const apiTrailers = await GetTrailersListAsync();
      const mapped = apiTrailers.map((tr) => mapAPITrailerToUI(tr, masterData, lockReasonCodes));
      setTrailers(mapped);
    } catch {
      setTrailers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Silent refetch for auto-refresh
  const silentRefetch = useCallback(async () => {
    try {
      const masterData = getTrailerMasterData();
      const lockReasonCodes = getLockReasonCodes();
      const apiTrailers = await GetTrailersListAsync();
      const mapped = apiTrailers.map((tr) => mapAPITrailerToUI(tr, masterData, lockReasonCodes));
      setTrailers(mapped);
    } catch {
      // Silent fail — keeps existing data
    }
  }, []);

  useEffect(() => {
    void loadTrailers();
  }, [loadTrailers]);

  // ── Auto-refresh timer — Matches Xamarin AutoRefreshTimer() ──
  useEffect(() => {
    const intervalMs = parseAutoRefreshInterval(autoRefreshScreens);
    if (intervalMs <= 0) return;
    canRefreshRef.current = true;
    const timer = setInterval(() => {
      if (canRefreshRef.current) {
        void silentRefetch();
      }
    }, intervalMs);
    return () => {
      canRefreshRef.current = false;
      clearInterval(timer);
    };
  }, [autoRefreshScreens, silentRefetch]);

  // Close legend on Escape
  useEffect(() => {
    if (!legendOpen) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLegendOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [legendOpen]);

  // ── Search filter — Matches Xamarin SearchCommandHandler ──
  const filteredTrailers = useMemo(() => {
    if (searchText.trim() === '') return trailers;
    const keyword = searchText.toUpperCase();
    return trailers.filter(
      (tr) =>
        tr.trailerNumber.toUpperCase().includes(keyword) ||
        tr.carrierName.toUpperCase().includes(keyword) ||
        tr.location.toUpperCase().includes(keyword) ||
        (tr.damageReason ?? 'NA').toUpperCase().includes(keyword)
    );
  }, [trailers, searchText]);

  const handleSearchChange = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  const handleInfoClick = useCallback((trailer: TrailerModel) => {
    setSelectedTrailer(trailer);
    setIsPopupOpen(true);
  }, []);

  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
  }, []);

  const handleMarkAsDamaged = useCallback((trailer: TrailerModel) => {
    setPendingDamageTrailer(trailer);
  }, []);

  // Matches Xamarin TrailerSubmitCommandHandler
  const handleDamageSubmit = useCallback(
    (lockReasonCode: number | undefined, _reasonName: string) => {
      if (!pendingDamageTrailer) return;

      const trailer = pendingDamageTrailer;
      const masterData = getTrailerMasterData();
      const damagedStatus = masterData?.Trailer_Status.find((s) => s.Status_Name === 'Damaged');
      const damagedCondition = masterData?.Trailer_Conditions_Types.find(
        (c) => c.condition_name === 'Damaged'
      );
      const statusCode = damagedStatus ? Number(damagedStatus.Status_Code) : 20;
      const conditionCode = damagedCondition ? damagedCondition.condition_code : 8;

      void updateRedTagTrailer({
        trailerName: trailer.trailerNumber,
        trailerId: trailer.trailerId,
        trailerCondition: conditionCode,
        trailerStatus: statusCode,
        carrierScac: trailer.carrierScac,
        lockReasonCode: lockReasonCode ?? 1,
      })
        .then(() => {
          showToast(t('trailers.updatedSuccess', { name: trailer.trailerNumber }));
          void loadTrailers();
        })
        .catch(() => {
          showToast(t('trailers.updateFailed', { name: trailer.trailerNumber }), {
            type: 'error',
          });
        });

      setPendingDamageTrailer(undefined);
    },
    [pendingDamageTrailer, loadTrailers, showToast, t]
  );

  const handleDamageClose = useCallback(() => {
    setPendingDamageTrailer(undefined);
  }, []);

  return (
    <div className="flex flex-col gap-[5px] pt-[5px] relative">
      {/* Search Bar */}
      <TrailerSearchBar searchText={searchText} onSearchChange={handleSearchChange} />

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-[#1357A0]" />
        </div>
      ) : filteredTrailers.length === 0 ? (
        <EmptyTrailerState />
      ) : (
        <>
          {/* ═══ Desktop Table (md+) — Matches Xamarin TrailersListPage 5-col ═══ */}
          <div className="mx-[10px] my-[5px] overflow-hidden rounded-[10px] border border-[#A1CDE8] bg-white hidden md:block">
            {/* Header — Matches Xamarin: bg #D7E7F3, height 50, Padding="20,5" */}
            {/* 6-col grid to align with body rows (auto col for status icon placeholder) */}
            <div className="grid grid-cols-[auto_1fr_1fr_1fr_1fr_auto] items-center bg-[#D7E7F3] px-5 py-[5px] min-h-[50px]">
              {/* Placeholder for status icon column */}
              <div className="w-[15px]" />
              <span className="text-sm lg:text-base xl:text-lg font-poppins font-bold text-[#003366] ml-[10px]">
                {t('trailers.trailerNumber')}
              </span>
              <span className="text-sm lg:text-base xl:text-lg font-poppins font-bold text-[#003366] ml-[18px]">
                {t('trailers.carrierName')}
              </span>
              <span className="text-sm lg:text-base xl:text-lg font-poppins font-bold text-[#003366] ml-[18px]">
                {t('trailers.location')}
              </span>
              <span className="text-sm lg:text-base xl:text-lg font-poppins font-bold text-[#003366] ml-[18px]">
                {t('trailers.damageReason')}
              </span>
              <span className="text-sm lg:text-base xl:text-lg font-poppins font-bold text-[#003366] text-center">
                {t('trailers.actions')}
              </span>
            </div>

            {/* Data rows */}
            <div
              className="overflow-y-auto bg-white"
              style={{
                maxHeight: 'calc(100vh - 220px)',
                scrollbarWidth: 'thin',
                scrollbarColor: '#D2DAE1 transparent',
              }}
            >
              {filteredTrailers.map((trailer, index) => (
                <div key={trailer.trailerId}>
                  {/* Separator — Xamarin: 1px, #C3D2DC, Margin="5,0,5,12" */}
                  {index > 0 && (
                    <div className="mx-[5px] mb-[12px]">
                      <div className="h-px bg-[#C3D2DC]" />
                    </div>
                  )}

                  {/* Data row — 6 cols: Auto,*,*,*,*,Auto */}
                  <div
                    className={cn(
                      'grid grid-cols-[auto_1fr_1fr_1fr_1fr_auto] items-center gap-x-[5px] mx-[10px] py-[10px]',
                      index === 0 ? 'pt-[12px]' : ''
                    )}
                  >
                    {/* Col 0: Status icon — 15x15 */}
                    <div className="mt-[10px]">
                      <StatusIcon status={trailer.status} />
                    </div>

                    {/* Col 1: Trailer Name + DMG badge + Info icon */}
                    <div className="flex items-center gap-1.5 mt-[10px] ml-[10px] min-w-0">
                      <span className="text-xs lg:text-sm xl:text-base font-poppins text-[#243743] truncate">
                        {trailer.trailerNumber}
                      </span>
                      {/* DMG badge — Matches Xamarin: bg=#15CF365A, text=#CF365A, PoppinsBold 13px */}
                      {trailer.status === 'Damaged' && (
                        <span className="bg-[#CF365A]/[0.08] border border-[#CF365A]/[0.08] text-[#CF365A] text-[11px] lg:text-[13px] font-bold font-poppins px-[5px] py-[2px] rounded-md shrink-0">
                          {t('trailers.dmgBadge')}
                        </span>
                      )}
                      {/* Info icon — 15x15 */}
                      <button
                        type="button"
                        onClick={() => {
                          handleInfoClick(trailer);
                        }}
                        className="p-0.5 hover:bg-blue-50 rounded-full transition-colors ml-[5px] shrink-0"
                        aria-label={`View info for ${trailer.trailerNumber}`}
                      >
                        <Info className="h-[15px] w-[15px] text-[#1357A0]" />
                      </button>
                    </div>

                    {/* Col 2: Carrier Name */}
                    <span className="text-xs lg:text-sm xl:text-base font-poppins text-[#243743] mt-[10px] min-w-0 truncate">
                      {trailer.carrierName}
                    </span>

                    {/* Col 3: Location */}
                    <span className="text-xs lg:text-sm xl:text-base font-poppins text-[#243743] mt-[10px] min-w-0 truncate">
                      {trailer.location}
                    </span>

                    {/* Col 4: Lock Reason Code Name */}
                    <span className="text-xs lg:text-sm xl:text-base font-poppins text-[#243743] mt-[10px] min-w-0 truncate">
                      {trailer.damageReason ?? t('trailers.notAvailable')}
                    </span>

                    {/* Col 5: Ellipsis action — 15x15 */}
                    <div className="mt-[10px] flex justify-end ml-[5px]">
                      <button
                        type="button"
                        onClick={() => {
                          setActionsTrailer(trailer);
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        aria-label={`Actions for ${trailer.trailerNumber}`}
                      >
                        <MoreVertical className="h-[15px] w-[15px] text-[#445E71]" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ═══ Mobile Table (below md) — Matches Xamarin TrailersListMobilePage 4-col ═══ */}
          <div className="mx-[10px] my-[5px] overflow-hidden rounded-[10px] border border-[#A1CDE8] bg-white md:hidden">
            {/* Header — 5 cols to match body (auto for icon placeholder) */}
            <div className="grid grid-cols-[auto_1fr_1fr_1fr_auto] items-center bg-[#D7E7F3] px-5 py-[5px] min-h-[50px]">
              {/* Placeholder for status icon column */}
              <div className="w-[15px]" />
              <span className="text-[10px] xs:text-xs sm:text-xs font-poppins font-bold text-[#003366] ml-[10px]">
                {t('trailers.trailerNumber')}
              </span>
              <span className="text-[10px] xs:text-xs sm:text-xs font-poppins font-bold text-[#003366] ml-[10px]">
                {t('trailers.carrierName')}
              </span>
              <span className="text-[10px] xs:text-xs sm:text-xs font-poppins font-bold text-[#003366] ml-[10px]">
                {t('trailers.location')}
              </span>
              <span className="text-[10px] xs:text-xs sm:text-xs font-poppins font-bold text-[#003366] text-center ml-[10px]">
                {t('trailers.actions')}
              </span>
            </div>

            {/* Data rows */}
            <div
              className="overflow-y-auto bg-white"
              style={{
                maxHeight: 'calc(100vh - 220px)',
                scrollbarWidth: 'thin',
                scrollbarColor: '#D2DAE1 transparent',
              }}
            >
              {filteredTrailers.map((trailer, index) => (
                <div key={trailer.trailerId}>
                  {/* Separator — 3px on phone */}
                  {index > 0 && (
                    <div className="mx-[5px] mb-[12px]">
                      <div className="h-[3px] bg-[#C3D2DC]" />
                    </div>
                  )}

                  {/* Data row — 5 cols: Auto,*,*,*,Auto (NO DMG badge on mobile) */}
                  <div
                    className={cn(
                      'grid grid-cols-[auto_1fr_1fr_1fr_auto] items-center gap-x-[3px] xs:gap-x-[5px] mx-[5px] xs:mx-[10px] py-[10px]',
                      index === 0 ? 'pt-[12px]' : ''
                    )}
                  >
                    <div className="mt-[10px]">
                      <StatusIcon status={trailer.status} />
                    </div>

                    {/* Trailer Name — NO DMG badge on mobile (Xamarin IsVisible="False") */}
                    <div className="flex items-center gap-1 mt-[10px] ml-[5px] xs:ml-[10px] min-w-0">
                      <span className="text-[10px] xs:text-xs font-poppins text-[#243743] truncate">
                        {trailer.trailerNumber}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          handleInfoClick(trailer);
                        }}
                        className="p-0.5 hover:bg-blue-50 rounded-full transition-colors shrink-0"
                        aria-label={`View info for ${trailer.trailerNumber}`}
                      >
                        <Info className="h-[13px] w-[13px] xs:h-[15px] xs:w-[15px] text-[#1357A0]" />
                      </button>
                    </div>

                    <span className="text-[10px] xs:text-xs font-poppins text-[#243743] mt-[10px] min-w-0 truncate">
                      {trailer.carrierName}
                    </span>

                    <span className="text-[10px] xs:text-xs font-poppins text-[#243743] mt-[10px] min-w-0 truncate">
                      {trailer.location}
                    </span>

                    <div className="mt-[10px]">
                      <button
                        type="button"
                        onClick={() => {
                          setActionsTrailer(trailer);
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        aria-label={`Actions for ${trailer.trailerNumber}`}
                      >
                        <MoreVertical className="h-[15px] w-[15px] text-[#445E71]" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ═══ Legend FAB — Matches Xamarin TrailerLegendInformationPopUp ═══ */}
      <div className="fixed bottom-3 right-3 xs:bottom-4 xs:right-4 md:bottom-6 md:right-6 z-10">
        {legendOpen && (
          <>
            <div
              className="fixed inset-0"
              onClick={() => {
                setLegendOpen(false);
              }}
              role="presentation"
            />
            {/* Legend popup — Matches Xamarin: Width 200/180, CornerRadius=22, bottom-right anchored */}
            <div
              className="absolute bottom-full right-0 mb-2 bg-white rounded-[22px] shadow-xl p-4 w-[180px] md:w-[200px]"
              role="dialog"
              aria-modal="true"
              aria-label={t('trailers.legend')}
            >
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-[18px] w-[18px] text-[#00D649] shrink-0 mx-[6px]" />
                  <span className="text-xs md:text-sm font-poppins font-medium text-[#333333] mx-[6px]">
                    {t('trailers.ibObLoaded')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Hourglass className="h-[18px] w-[18px] text-[#EDBE18] shrink-0 mx-[6px]" />
                  <span className="text-xs md:text-sm font-poppins font-medium text-[#333333] mx-[6px]">
                    {t('trailers.ibObLoading')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Circle className="h-[18px] w-[18px] text-[#333333] shrink-0 mx-[6px]" />
                  <span className="text-xs md:text-sm font-poppins font-medium text-[#333333] mx-[6px]">
                    {t('trailers.empty')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Ban className="h-[18px] w-[18px] text-[#FC3B3B] shrink-0 mx-[6px]" />
                  <span className="text-xs md:text-sm font-poppins font-medium text-[#333333] mx-[6px]">
                    {t('trailers.damaged')}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
        {/* FAB button — Matches Xamarin: height 43, width 100, CornerRadius=20, shadow */}
        <button
          type="button"
          onClick={() => {
            setLegendOpen(!legendOpen);
          }}
          className="flex items-center gap-1 bg-white rounded-full h-[43px] w-[100px] shadow-md border border-gray-200 hover:bg-gray-50 transition-colors px-[5px]"
          aria-label={t('trailers.showLegend')}
        >
          <HelpCircle className="h-[18px] w-[18px] text-[#657E90] ml-[15px]" />
          <span className="text-xs md:text-sm font-poppins font-medium text-[#657E90]">
            {t('trailers.legend')}
          </span>
        </button>
      </div>

      {/* Actions Popup */}
      <ActionsPopup
        open={actionsTrailer !== undefined}
        trailer={actionsTrailer}
        onClose={() => {
          setActionsTrailer(undefined);
        }}
        onMarkAsDamaged={() => {
          if (actionsTrailer) {
            handleMarkAsDamaged(actionsTrailer);
          }
          setActionsTrailer(undefined);
        }}
      />

      {/* Trailer Information Popup */}
      {selectedTrailer && (
        <TrailerInformationPopup
          trailer={selectedTrailer}
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
        />
      )}

      {/* Mark As Damaged Dialog */}
      <MarkAsDamagedDialog
        open={pendingDamageTrailer !== undefined}
        trailer={pendingDamageTrailer}
        onClose={handleDamageClose}
        onSubmit={handleDamageSubmit}
      />
    </div>
  );
}
